import React, { useEffect, useState } from "react";
import TdLink from "../../components/TdLink";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsis,
    faPenToSquare,
    faCircleMinus,
    faPlus,
} from "@fortawesome/pro-regular-svg-icons";

import { RoleLabelsForExternal } from "../../providers/AuthContext";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllExternalUsers, deleteExternalUser } from "../../api/ExternalUsers";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingSpinner from "../../components/LoadingSpinner";
import DataTable from "../../components/DataTable";
import { formatTermsAccepted } from "../../components/externalUser/externalUserUtils";
import { useTranslation } from "react-i18next";

const ExternalUsersPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();

    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);

    const [search, setSearch] = useState("");

    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        confirm: () => { },
    });

    useEffect(() => {
        handleGetUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    const handleGetUsers = async () => {
        setUsersLoaded(false);
        try {
            const accessToken = await getAccessToken();
            const newUsers = await getAllExternalUsers(accessToken);

            const mapped = (Array.isArray(newUsers) ? newUsers : []).map((u) => ({
                ...u,
                organizationName: u?.organization?.organizationName ?? "-",
                organizationPublicId: u?.organization?.organizationPublicId ?? "",
            }));

            setUsers(mapped);
        } catch (error) {
            console.error(error);
            toast.error(error?.message || t("toast.failedFetch", { entity: t("entity.users") }));
            setUsers([]);
        } finally {
            setUsersLoaded(true);
        }
    };


    const handleDeleteUser = async (externalUserPublicId) => {
        const toastId = toast.loading(t("toast.deleting", { entity: t("entity.externalUser") }));
        try {
            const accessToken = await getAccessToken();
            await deleteExternalUser(externalUserPublicId, accessToken);

            setUsers((prev) => prev.filter((u) => u.externalUserPublicId !== externalUserPublicId));

            toast.success(t("toast.deleted", { entity: t("entity.externalUser") }), { id: toastId });
        } catch (error) {
            toast.error(t("toast.failedDelete", { entity: t("entity.externalUser") }), { id: toastId });
            console.error(error);
        }
    };

    const columns = [
        {
            key: "externalUserPublicId",
            header: t("table.userId"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) => user.externalUserPublicId ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {user.externalUserPublicId || ""}
                </TdLink>
            ),
        },
        {
            key: "email",
            header: t("common.email"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) => user.email ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {user.email || ""}
                </TdLink>
            ),
        },
        {
            key: "firstName",
            header: t("common.firstName"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) => user.firstName ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {user.firstName || ""}
                </TdLink>
            ),
        },
        {
            key: "lastName",
            header: t("common.lastName"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) => user.lastName ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {user.lastName || ""}
                </TdLink>
            ),
        },
        {
            key: "externalUserRole",
            header: t("common.role"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) =>
                RoleLabelsForExternal[user.externalUserRole] ?? user.externalUserRole ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {t(`role.${user.externalUserRole}`, {
                        defaultValue: RoleLabelsForExternal[user.externalUserRole] ?? user.externalUserRole ?? "",
                    })}
                </TdLink>
            ),
        },
        {
            key: "organizationName",
            header: t("entity.organization"),
            sortable: true,
            rawCell: true,
            hidden: true,
            getSortValue: (user) => user.organizationName ?? "",
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {user.organizationName}
                </TdLink>
            ),
        },
        {
            key: "termsAccepted",
            header: t("table.termsAccepted"),
            sortable: true,
            rawCell: true,
            getSortValue: (user) => (user.termsAccepted === true ? 1 : 0),
            render: (user) => (
                <TdLink to={`/externalUser/${user.externalUserPublicId}`}>
                    {formatTermsAccepted(user.termsAccepted, t)}
                </TdLink>
            ),
        },
        {
            key: "actions",
            header: "",
            sortable: false,
            searchable: false,
            rawCell: true,
            headerClassName: "min-w-[64px]",
            render: (user) => (
                <td>
                    <div className="flex justify-end">
                        <CustomDropdown
                            isRight={true}
                            ContentComponent={() => (
                                <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <NavLink
                                        className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                        to={`/externalUser/${user.externalUserPublicId}`}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                    </NavLink>

                                    <button
                                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                        onClick={() =>
                                            setConfirmState({
                                                isOpen: true,
                                                title: t("confirm.deleteEntity", {
                                                    entity: t("entity.externalUser"),
                                                    name: user.email,
                                                }),
                                                confirm: () =>
                                                    handleDeleteUser(user.externalUserPublicId),
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                </div>
                            )}
                        >
                            <div className="w-8 h-6 flex justify-end items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                            </div>
                        </CustomDropdown>
                    </div>
                </td>
            ),
        },
    ];

    if (!usersLoaded) {
        return (
            <>
                <HeaderBar />
                <div className="flex">
                    <Sidebar />
                    <main className="page flex items-center justify-center">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.externalUsers") })} />
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-col">
                <div className="flex flex-row">
                    <Sidebar />

                    <main className="page">
                        <div className="page-content">
                            <div className="flex justify-between items-center">
                                <h2 className="text-2xl text-black font-bold pb-2">
                                    {t("pages.externalHubUsers")}
                                </h2>

                                <NavLink className="btn btn-primary" to={`/externalUser/create`}>
                                    <FontAwesomeIcon icon={faPlus} /> {t("pages.createExternalUser")}
                                </NavLink>
                            </div>

                            <div className="rounded-md shadow-md w-full p-4">
                                <DataTable
                                    rows={users}
                                    columns={columns}
                                    search={search}
                                    onSearchChange={setSearch}
                                    searchPlaceholder={t("search.byIdNameOrEmail")}
                                    defaultSortColumn="externalUserPublicId"
                                    rowKey={(user, index) => user.externalUserPublicId || index}
                                    emptyColSpan={8}
                                    emptyContent={t("common.noEntityFound", { entity: t("entity.users") })}
                                />
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                title={confirmState.title}
                onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
                onConfirm={() => {
                    setConfirmState((s) => ({ ...s, isOpen: false }));
                    confirmState.confirm();
                }}
            />
        </>
    );
};

export default ExternalUsersPage;
